import { useState } from 'react';
import type { Board } from '@/lib/api/types';
import { useFieldPolicies, useFields, useSetFieldPolicies } from '@/lib/api/queries';
import { useSessionStore } from '@/stores/session';
import { usePermission } from '@/features/workspace/usePermission';
import { Dialog } from '@/components/Dialog';
import { Button } from '@/components/Button';
import { Select } from '@/components/Input';
import { Spinner } from '@/components/Misc';
import { toast } from '@/stores/ui';
import { isApiError } from '@/lib/api/errors';
import { FieldValueInput } from './FieldValueInput';
import { describeValue, draftFromPolicies, policiesFromDraft, type FieldDraft, type FieldRule, type PoliciesDraft } from './policy';

const RULE_LABEL: Record<Exclude<FieldRule, 'inherit'>, string> = { optional: 'Optional', required: 'Required', hidden: 'Hidden' };

/**
 * Board settings › Fields (Release 2 G3): per asset field, whether it is required, optional or hidden for assets of
 * this board and its sub-boards, plus a default applied when an asset lands here without a value. Rows left on
 * "Inherit" follow the nearest parent board with a rule, else the workspace field definition.
 */
export function FieldPoliciesDialog({ board, onClose }: { board: Board; onClose: () => void }) {
  const wsId = useSessionStore((s) => s.workspace?.id);
  const fields = useFields(wsId);
  const policies = useFieldPolicies(board.id);
  const canManage = usePermission('MANAGE_BOARD', { boardId: board.id });
  const save = useSetFieldPolicies(board.id);
  const [draft, setDraft] = useState<PoliciesDraft | null>(null);
  const current = draft ?? draftFromPolicies(policies.data);
  const smart = board.kind === 'smart';
  const list = (fields.data ?? []).filter((f) => f.applies_to === 'asset');
  const effective = new Map((policies.data?.effective ?? []).map((e) => [e.field_id, e]));
  const boardName = (id: string | null | undefined) => (id ? ((board.breadcrumbs ?? []).find((b) => b.id === id)?.name ?? 'a parent board') : null);
  const patch = (fieldId: string, next: Partial<FieldDraft>) => setDraft({ ...current, [fieldId]: { rule: 'inherit', default_value: null, ...current[fieldId], ...next } });
  const submit = () =>
    save.mutate(policiesFromDraft(current, policies.data?.effective ?? [], board.id), {
      onSuccess: () => {
        toast({ kind: 'success', title: `Field rules saved for “${board.name}”` });
        onClose();
      },
      onError: (e) => toast({ kind: 'error', title: isApiError(e) ? e.message : 'Could not save field rules' }),
    });
  return (
    <Dialog
      open
      onClose={onClose}
      width="max-w-2xl"
      title={`Fields — ${board.name}`}
      footer={
        <>
          <Button onClick={onClose}>{canManage && !smart ? 'Cancel' : 'Close'}</Button>
          {canManage && !smart && (
            <Button variant="primary" onClick={submit} disabled={save.isPending || !draft} data-save-field-policies>
              Save
            </Button>
          )}
        </>
      }
    >
      {smart ? (
        <p className="text-sm text-fg-muted">Smart boards hold no assets of their own, so they have no field rules.</p>
      ) : fields.isPending || policies.isPending ? (
        <Spinner />
      ) : list.length === 0 ? (
        <p className="text-sm text-fg-muted">This workspace has no asset fields yet. Add them under Settings › Fields.</p>
      ) : (
        <>
          <p className="mb-3 text-xs text-fg-subtle">
            Rules apply to this board and its sub-boards. Assets missing a required field get an amber corner and cannot be approved until it is filled. A field is hidden only when every board an asset is in hides it.
          </p>
          <table className="w-full text-sm" data-field-policies>
            <thead className="text-left text-xs text-fg-subtle uppercase">
              <tr>
                <th className="pb-2 pr-3 font-medium">Field</th>
                <th className="pb-2 pr-3 font-medium">Rule</th>
                <th className="pb-2 font-medium">Default</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {list.map((f) => {
                const d = current[f.id] ?? { rule: 'inherit' as const, default_value: null };
                const eff = effective.get(f.id);
                const from = eff && eff.source_board_id !== board.id ? boardName(eff.source_board_id) : null;
                const inheritedRule = eff && eff.source_board_id !== board.id ? (eff.required ? 'Required' : eff.hidden ? 'Hidden' : 'Optional') : f.required ? 'Required' : 'Optional';
                const inheritedDefault = eff && eff.source_board_id !== board.id ? describeValue(f, eff.default_value) : '';
                return (
                  <tr key={f.id} data-field-policy={f.id}>
                    <td className="py-1.5 pr-3 align-top">
                      <div className="font-medium">{f.name}</div>
                      <div className="text-[11px] text-fg-subtle">{f.type.replace('_', ' ')}</div>
                    </td>
                    <td className="py-1.5 pr-3 align-top">
                      <Select aria-label={`Rule for ${f.name}`} className="h-7 text-xs" value={d.rule} disabled={!canManage} onChange={(e) => patch(f.id, { rule: e.target.value as FieldRule })}>
                        <option value="inherit">Inherit ({inheritedRule}{from ? ` · ${from}` : ''})</option>
                        {(Object.keys(RULE_LABEL) as Array<keyof typeof RULE_LABEL>).map((r) => <option key={r} value={r}>{RULE_LABEL[r]}</option>)}
                      </Select>
                    </td>
                    <td className="py-1.5 align-top">
                      {canManage && d.rule !== 'hidden' ? (
                        <FieldValueInput field={f} value={d.default_value} onChange={(v) => patch(f.id, { default_value: v })} />
                      ) : (
                        <span className="text-xs text-fg-muted">{describeValue(f, d.default_value) || '—'}</span>
                      )}
                      {!d.default_value && inheritedDefault && d.rule === 'inherit' && <div className="mt-0.5 text-[11px] text-fg-subtle">Inherited default: {inheritedDefault}</div>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </Dialog>
  );
}
